<template>
    <b-row class="pb-2">
        <parameter-tooltip :parameter-key="paramKey" :parameter-description="description "></parameter-tooltip>

        <!--Parameter Name-->
        <b-col id="parameter-name" style="padding-top: 0.2rem;" cols="7" sm="3" md="3" lg="3" xl="3">
            <label>{{ name }}</label>
        </b-col>
        <!------------------>

        <!--Parameter Input-->
        <b-col v-if="isReadOnly(paramKey)" class="pl-3 pb-4 readonly-alignment" cols="1" sm="2" md="2" lg="2" xl="1">
            <read-only :parameter-key="paramKey" :parameter-value="parameterValue"></read-only>
        </b-col>

        <b-col v-else-if="type === 'offDailyWeekly'" class="pl-3 pb-4 radio-button-alignment" cols="10" sm="6" md="4" lg="4" xl="4">
            <radio-button-group :parameter-key="paramKey" v-model="parameterValue" :radio-options="['Off', 'Daily', 'Weekly']"></radio-button-group>
        </b-col>

        <b-col v-else-if="type === 'boolean'" class="pl-3 pb-4 radio-button-alignment" cols="6" sm="4" md="3" lg="3" xl="2">
            <radio-button-group :parameter-key="paramKey" v-model="parameterValue" :radio-options="['Off', 'On']"></radio-button-group>
        </b-col>

        <b-col v-else-if="type === 'timezone'" class="pl-3 py-2 py-sm-1" cols="6" sm="4" md="3" lg="3" xl="2">
            <timezone :parameter-key="paramKey" v-bind:value.sync="parameterValue"></timezone>
        </b-col>

        <b-col v-else class="pl-3 pb-4" cols="10" sm="7" md="7" lg="7" xl="5">
            <div v-if="paramKey === 'p_1'" style="padding-top: 0.2rem;">
                <variant-slider :parameter-key="paramKey" v-bind:value.sync="parameterValue" :min="min" :max="max" :pending="pending">
                </variant-slider>
            </div>

            <div v-else-if="type === 'slider'" style="padding-top: 0.2rem;">
                <slider v-if="fahrenheitModeActive && isFahrenheitParameter" :parameter-key="paramKey" v-bind:value.sync="parameterValue" :min="fmin"
                        :max="fmax"></slider>
                <slider v-else :parameter-key="paramKey" v-bind:value.sync="parameterValue" :min="min" :max="max"></slider>
            </div>

            <div v-else-if="type === 'setpoint'" class="readonly-alignment">
                <slider v-if="shouldRenderSetpointSlider() && fahrenheitModeActive && isFahrenheitParameter" :parameter-key="paramKey"
                        v-bind:value.sync="parameterValue" :min="fmin" :max="fmax"></slider>

                <slider v-else-if="shouldRenderSetpointSlider()" :parameter-key="paramKey" v-bind:value.sync="parameterValue" :min="min"
                        :max="max"></slider>

                <strong v-else>{{ setpointValue }}</strong>
            </div>

            <div v-else-if="type === 'offOnSchedule'" class="radio-button-alignment">
                <radio-button-group :parameter-key="paramKey" v-model="parameterValue"
                                    :radio-options="['Off', 'On', 'Schedule']"></radio-button-group>
            </div>

            <div v-else-if="type === 'relayPump'" class="radio-button-alignment">
                <b-form-radio-group :id="'input_' + paramKey" :name="paramKey" v-model="parameterValue">
                    <b-form-radio :value="0">Standard</b-form-radio>
                    <b-form-radio :value="1" :disabled="!freeAnalogueControllerExists">V1</b-form-radio>
                </b-form-radio-group>
            </div>

            <div v-else-if="type === 'pumpType'" class="radio-button-alignment">
                <radio-button-group :parameter-key="paramKey" v-model="parameterValue" :radio-options="['PWM', '0-10V']"></radio-button-group>
            </div>

            <div v-else-if="type === 'pumpSignal'" class="radio-button-alignment">
                <radio-button-group :parameter-key="paramKey" v-model="parameterValue" :radio-options="['Inverted', 'Normal']"></radio-button-group>
            </div>

            <div v-else-if="type === 'moduleType'" class="radio-button-alignment">
                <radio-button-group :parameter-key="paramKey" v-model="parameterValue"
                                    :radio-options="['Disabled', 'Thermostat', 'Recycle', 'On/Off']">
                </radio-button-group>
            </div>

            <div v-else-if="type === 'storageSensor'" class="radio-button-alignment">
                <radio-button-group :parameter-key="paramKey" v-model="parameterValue" :radio-options="['S1', 'S2', 'S3', 'S4']"></radio-button-group>
            </div>

            <div v-else-if="type === 'moduleSensor'" class="radio-button-alignment">
                <radio-button-group :parameter-key="paramKey" v-model="parameterValue" :radio-options="['S1', 'S2', 'S3', 'S4']"></radio-button-group>
            </div>

            <div v-else-if="type === 'moduleRelay'"></div>

            <div v-else-if="type === 'mode'" class="radio-button-alignment">
                <radio-button-group :parameter-key="paramKey" v-model="parameterValue" :radio-options="['Heat', 'Cool']"></radio-button-group>
            </div>

            <div v-else-if="type === 'lowHigh'" class="radio-button-alignment">
                <radio-button-group :parameter-key="paramKey" v-model="parameterValue" :radio-options="[{index: '1', name: 'Low'},
                                    {index: '2', name: 'High'}]"></radio-button-group>
            </div>

            <div v-else-if="type === 'sensorType'" class="radio-button-alignment">
                <radio-button-group :parameter-key="paramKey" v-model="parameterValue" :radio-options="['PT1000', 'NTC 10K']"></radio-button-group>
            </div>

            <div v-else-if="type === 'betaCoefficient'">
                <beta-coefficient v-bind:value.sync="value" :min="min" :max="max"></beta-coefficient>
            </div>

            <div v-else-if="type === 'sensorUnits'" class="radio-button-alignment">
                <radio-button-group :parameter-key="paramKey" v-model="parameterValue" :radio-options="['°C', '°F']"></radio-button-group>
            </div>

            <div v-else-if="!type">
                <input type="number" autocomplete="off" class="form-control input-sm" :id="'input_' + paramKey" :name="paramKey"
                       v-model="parameterValue">
            </div>
        </b-col>
        <!------------------->

        <b-col class="py-2 py-sm-1" cols="1" sm="1" md="1" lg="1" xl="1" :id="'pending_' + paramKey" v-show="pending || showWarning">
            <parameter-spinner :parameter-key="paramKey" :show-warning="showWarning"></parameter-spinner>
        </b-col>
    </b-row>
</template>

<script>
    import {mapGetters} from 'vuex';
    import ParameterTooltip from "./ParameterTooltip";
    import ParameterSpinner from "./ParameterSpinner";
    import ReadOnly from "./ReadOnly";
    import RadioButtonGroup from "./RadioButtonGroup";
    import BetaCoefficient from "./BetaCoefficient";
    import Timezone from "./Timezone";
    import Slider from "./Slider";
    import VariantSlider from "./VariantSlider";
    import {isReadOnlyV2, getSetpointValue} from "@/utilities/parameter.utility";

    export default {
        name: "EditParameter",

        components: {
            Timezone,
            Slider,
            VariantSlider,
            ParameterTooltip,
            ParameterSpinner,
            ReadOnly,
            RadioButtonGroup,
            BetaCoefficient
        },

        props: {
            paramKey: String,
            value: [Number, String, Object],
            name: String,
            type: String,
            description: String,
            min: Number,
            max: Number,
            fmin: Number,
            fmax: Number,
            pending: Boolean,
            showWarning: Boolean,
            freeAnalogueControllerExists: {
                type: Boolean,
                default: true
            }
        },

        data() {
            return {}
        },

        computed: {
            ...mapGetters({
                getParameters: 'virtualDevice/parameters'
            }),

            parameters: function () {
                return this.getParameters(this.$route.params.id);
            },

            setpointValue() {
                return getSetpointValue(this.paramKey, this.parameters);
            },

            parameterValue: {
                get: function () {
                    return this.value;
                },

                set: function (value) {
                    this.$emit('update:value', value)
                }
            },

            fahrenheitModeActive() {
                return this.parameters.p_186.value == 1;
            },

            isFahrenheitParameter() {
                return this.fmin;
            }
        },

        methods: {
            isReadOnly(key) {
                return isReadOnlyV2(key);
            },

            shouldRenderSetpointSlider() {
                if (this.paramKey === 'p_35') return parseInt(this.parameters.p_101.value) !== 2;
                else if (this.paramKey === 'p_87') return parseInt(this.parameters.p_105.value) !== 2;
                else if (this.paramKey === 'p_94') return parseInt(this.parameters.p_109.value) !== 2;
                else if (this.paramKey === 'p_39') return parseInt(this.parameters.p_103.value) !== 2;
                else if (this.paramKey === 'p_91') return parseInt(this.parameters.p_107.value) !== 2;
                else if (this.paramKey === 'p_98') return parseInt(this.parameters.p_111.value) !== 2;
            }
        }
    }
</script>

<style scoped>
    .radio-button-alignment {
        padding-top: 0.3rem;
    }

    .readonly-alignment {
        padding-top: 0.3rem;
    }
</style>















